// Small pieces the Building and Property overviews share: a section head, a quiet line, a footer link, the
// certificates chip and the "show all" toggle.
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import type { CertRoll } from '../../lib/portfolio';

export function SectionHead({ title, note }: { title: string; note?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-ink-100 px-4 py-3">
      <h3 className="text-[13px] font-semibold text-ink-900">{title}</h3>
      {note && <span className="text-[11.5px] tabular-nums text-ink-400">{note}</span>}
    </div>
  );
}
export const Quiet = ({ children }: { children: ReactNode }) => <p className="px-4 py-6 text-center text-[12.5px] text-ink-500">{children}</p>;
export const FootLink = ({ to, children }: { to: string; children: string }) => (
  <Link to={to} className="flex items-center justify-center gap-1 border-t border-ink-100 px-4 py-2 text-[12px] font-medium text-bronze-600 hover:bg-ink-50">{children} <ArrowRight className="h-3 w-3" /></Link>
);
export function ShowAll({ all, total, onToggle }: { all: boolean; total: number; onToggle: () => void }) {
  return (
    <button type="button" onClick={onToggle} className="flex w-full items-center justify-center gap-1 border-t border-ink-100 px-4 py-2 text-[12px] font-medium text-bronze-600 hover:bg-ink-50">
      {all ? 'Show fewer' : `Show all ${total}`} <ArrowRight className={`h-3 w-3 transition ${all ? '-rotate-90' : 'rotate-90'}`} />
    </button>
  );
}
export function Th({ on, children, left }: { on?: boolean; children: string; left?: boolean }) {
  return <th className={`px-3 py-1.5 ${left ? 'text-left' : 'text-right'} ${on ? 'text-bronze-600' : ''}`}>{children}</th>;
}
const pill = 'inline-flex items-center gap-1 rounded-full px-2 py-[2px] text-[11px] font-medium';
/** The demo's certificates cell: expired, else due, else in date. */
export function CertChip({ c }: { c: CertRoll | null }) {
  if (!c) return <span className="text-ink-300">—</span>;
  if (c.expired) return <span className={`${pill} bg-red-50 text-red-700`}><i className="h-1.5 w-1.5 rounded-full bg-red-500" />{c.expired} expired</span>;
  if (c.due) return <span className={`${pill} bg-amber-50 text-amber-700`}><i className="h-1.5 w-1.5 rounded-full bg-amber-500" />{c.due} due</span>;
  if (c.valid) return <span className={`${pill} bg-emerald-50 text-emerald-700`}><i className="h-1.5 w-1.5 rounded-full bg-emerald-500" />in date</span>;
  return <span className="text-ink-300">—</span>;
}
export const Count = ({ n, tone }: { n: number | null; tone?: 'critical' | 'warning' }) =>
  n === null ? <span className="text-ink-300">—</span> : !n ? <span className="text-ink-300">0</span>
    : tone ? <span className={`${pill} ${tone === 'critical' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>{n}</span> : <span>{n}</span>;
